import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  inject,
  input,
} from '@angular/core';

import { DonutChartComponent } from '../charts/donut-chart.component';
import { ReportChromeService } from '../report-chrome.service';
import { ReportFmtPipe, ReportTPipe } from '../report-i18n.pipe';
import { ReportI18nService } from '../report-i18n.service';
import { ReportRevealDirective } from '../report-reveal.directive';
import { ReportLevel, SubjectReportView } from '../report.model';

/** Nom du plan associé à un palier — voir la note de `subject-levels.section.ts`. */
const PLAN_KEY: Record<ReportLevel, string> = {
  weak: 'remedial',
  average: 'improvement',
  good: 'development',
  excellent: 'gifted',
};

/** Une entrée de légende de l'anneau. */
interface DonutLegendEntry {
  key: string;
  label: string;
  color: string;
  count: number;
  /** Part rapportée au **total des compétences**, non au total de l'anneau. */
  percentage: number;
}

/**
 * Section « acquisition des compétences » d'une matière.
 *
 * Elle compte des compétences, jamais des points : aucun /20 ici, il appartient
 * au résumé. Les trois cartes et l'anneau partagent le même dénominateur —
 * `acquisition.total`, qui inclut les compétences non évaluées — de sorte que
 * les parts affichées s'additionnent à 100 %.
 *
 * **Plus de recoloration après coup.** Le document d'origine repeignait les
 * pastilles de légende en parcourant le DOM à chaque bascule de thème. Les
 * couleurs sont désormais une donnée de vue, portée par les secteurs du modèle,
 * et changer de thème recalcule la vue.
 *
 * Hôte en `display: contents`, comme les autres sections : `.report-main` est
 * une boîte flex à gouttière, l'élément de composant en deviendrait l'enfant à
 * la place de la `<section>`.
 */
@Component({
  selector: 's2e-report-overview',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReportFmtPipe, ReportTPipe, ReportRevealDirective, DonutChartComponent],
  templateUrl: './subject-overview.section.html',
  host: { '[style.display]': "'contents'" },
})
export class SubjectOverviewSection {
  private readonly i18n = inject(ReportI18nService);
  protected readonly chrome = inject(ReportChromeService);

  readonly subject = input.required<SubjectReportView>();

  /**
   * Le dénominateur du sous-titre.
   *
   * <p>Il était <b>écrit en dur dans les trois fichiers de langue</b> — « sur
   * les 26 évaluées » — et tout rapport annonçait donc 26, quel que soit le
   * référentiel de la matière. Un bilan à neuf compétences se contredisait en
   * deux lignes : le sous-titre disait 26, les cartes juste dessous disaient
   * 9.</p>
   *
   * <p>Le libellé disait aussi « évaluées », alors que ce total est celui du
   * <b>référentiel de la matière</b> — il inclut les compétences qu'aucune
   * question n'a interrogées, que la troisième carte compte à part. C'est le
   * dénominateur commun aux trois cartes et à l'anneau ; le mot est corrigé
   * avec le nombre.</p>
   */
  protected readonly subtitleVars = computed(() => ({
    total: { n: this.subject().acquisition.total },
  }));

  /**
   * Légende de l'anneau.
   *
   * Les effectifs et les couleurs viennent des secteurs — c'est ce que le
   * graphique dessine — mais les **parts** viennent de `acquisition` : l'anneau
   * ne connaît que les compétences acquises et non acquises, alors que le
   * rapport rapporte tout au référentiel complet. Recalculer la part sur le
   * total de l'anneau donnerait un troisième pourcentage, en désaccord avec les
   * cartes juste à côté dès qu'une compétence n'est pas évaluée.
   */
  protected readonly legend = computed<DonutLegendEntry[]>(() => {
    const acquisition = this.subject().acquisition;

    return this.subject().charts.donut.map((slice) => ({
      key: slice.key,
      label: slice.label,
      color: slice.color,
      count: slice.value,
      percentage:
        slice.key === 'acquired'
          ? acquisition.acquiredPercentage
          : acquisition.notAcquiredPercentage,
    }));
  });

  /**
   * Libellé accessible de l'anneau.
   *
   * Le graphique ne traduit rien : la section lui remet une phrase déjà
   * résolue, sur le motif du document d'origine — « libellé effectif », les
   * secteurs séparés par une virgule.
   */
  protected readonly donutAria = computed(() =>
    this.i18n.t('chart.ariaDonut', {
      summary: this.subject()
        .charts.donut.map((slice) => `${slice.label} ${this.i18n.num(slice.value)}`)
        .join(', '),
    }),
  );

  /* Mise en forme confiée au graphique pour ses infobulles : c'est la section
     qui tient la locale, lui n'en sait rien. */
  protected readonly countFormat = computed<(value: number) => string>(() => {
    this.i18n.lang();
    return (value: number) => this.i18n.num(value);
  });

  protected readonly percentFormat = computed<(value: number) => string>(() => {
    this.i18n.lang();
    return (value: number) => this.i18n.pct(value);
  });

  /** Effectif du référentiel, pour la note de bas de carte. */
  protected readonly totalVars = computed(() => ({
    count: this.subject().acquisition.total,
  }));

  /**
   * Prochaine étape recommandée.
   *
   * La maquette y écrivait la description du plan d'amélioration en dur — le
   * palier de son jeu d'essai. Elle découle du palier atteint, comme la
   * pastille du résumé.
   */
  protected readonly planDescription = computed(() => {
    const level = this.subject().score.level;
    return level ? this.i18n.t(`levelScale.planDescriptions.${PLAN_KEY[level]}`) : '';
  });
}
